import { Info } from 'lucide-react';

import { Badge } from '@/shared/components/shadcn/badge';
import { Progress } from '@/shared/components/shadcn/progress';
import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { cn } from '@/shared/utils/cn';
import { getUsagePercent } from '../helpers/usage-utils';

export interface WorkspaceLimitItem {
  label: string;
  helper: string;
  value: string;
  usage?: {
    current: number;
    limit: number;
  };
  useProgressBar: boolean;
}

interface WorkspaceLimitRowProps {
  item: WorkspaceLimitItem;
  loading?: boolean;
}

export function WorkspaceLimitRow({ item, loading = false }: WorkspaceLimitRowProps) {
  const usagePercent =
    !loading && item.useProgressBar && item.usage
      ? getUsagePercent(item.usage.current, item.usage.limit)
      : null;
  const isDestructiveUsage = usagePercent !== null && usagePercent >= 90;

  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex items-center gap-4">
        <div className="flex w-80 min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{item.label}</span>
            <TooltipFlat content={item.helper}>
              <Info className="size-3.5" />
            </TooltipFlat>
            {isDestructiveUsage && (
              <Badge
                variant="destructive"
                className="rounded-md px-2 py-0.5 text-[11px] font-medium"
              >
                Near limit
              </Badge>
            )}
          </div>
        </div>
        <ToggleStage
          className="flex min-h-6 items-center gap-4"
          status={!loading}
          fallback={<Skeleton className="h-4 w-60 rounded-md" />}
        >
          {usagePercent !== null ? (
            <div className="relative flex w-80 flex-col gap-2">
              <Progress
                value={usagePercent}
                className={cn(
                  'h-1.5 bg-zinc-300',
                  isDestructiveUsage && '**:data-[slot=progress-indicator]:bg-red-400',
                )}
              />
              <div className="text-muted-foreground absolute flex w-full translate-y-3 justify-between text-xs">
                <span>{item.usage?.current.toLocaleString()} used</span>
                <span>{item.usage?.limit.toLocaleString()} total</span>
              </div>
            </div>
          ) : null}
          {!item.useProgressBar && <span className="text-sm whitespace-nowrap">{item.value}</span>}
        </ToggleStage>
      </div>
    </div>
  );
}
